import { useEffect, useRef, type ReactNode } from "react";
import s from "./largetitle.module.css";

export interface LargeTitleProps {
  eyebrow?: ReactNode;
  title: string;
  /** Called with `true` once the h1 has scrolled under the top bar, `false` when it comes back. */
  onUnderBarChange?: (under: boolean) => void;
  /** Height of the top bar the h1 scrolls under, in px. */
  offset?: number;
  action?: ReactNode;
}

/** Eyebrow + 30px/700 h1. An IntersectionObserver tells the top bar when to go solid and show the small title. */
export function LargeTitle({ eyebrow, title, onUnderBarChange, offset = 52, action }: LargeTitleProps) {
  const ref = useRef<HTMLHeadingElement>(null);
  const cb = useRef(onUnderBarChange);
  cb.current = onUnderBarChange;
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry) cb.current?.(!entry.isIntersecting && entry.boundingClientRect.top < offset);
    }, { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 });
    io.observe(el);
    return () => { io.disconnect(); cb.current?.(false); };
  }, [offset]);
  return (
    <div className={s.lt}>
      <div>
        {eyebrow ? <p className={s.eb}>{eyebrow}</p> : null}
        <h1 ref={ref} tabIndex={-1}>{title}</h1>
      </div>
      {action}
    </div>
  );
}
